import React, { useState, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';

/**
 * LoginSeguro - Componente de UI pura para tela de login
 * 
 * Props:
 * - onSubmitEmailPassword: (email, password) => void
 * - onGoogleClick?: () => void
 * - loading: boolean
 * - errorMessage?: string | null
 * - initialEmail?: string
 * - logoSrc?: string
 */
const LoginSeguro = ({
  onSubmitEmailPassword,
  onGoogleClick,
  loading = false,
  errorMessage = null,
  initialEmail = '',
  logoSrc = '/logo.png'
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [particles, setParticles] = useState([]);
  const [fadeIn, setFadeIn] = useState(false);

  // Generate particles on mount
  useEffect(() => {
    setFadeIn(true);
    
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 60; i++) {
        newParticles.push({
          id: i,
          left: Math.random() * 100,
          size: Math.random() * 4 + 2,
          duration: Math.random() * 10 + 15,
          delay: Math.random() * 5
        });
      }
      setParticles(newParticles);
    };
    
    generateParticles();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!loading) {
      onSubmitEmailPassword(email, password);
    }
  };

  return (
    <>
      <style>{`
        /* Fundo principal com gradiente preto */
        .login-container-seguro {
          min-height: 100vh;
          background: linear-gradient(135deg, #0a0a0a 0%, #1c1c1c 50%, #0a0a0a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          position: relative;
          overflow: hidden;
        }

        /* Camada 0: Grid branco sutil */
        .grid-background {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          z-index: 0;
        }

        /* Camada 1: Luzes vermelhas */
        .red-glow-top-left {
          position: absolute;
          top: -200px;
          left: -200px;
          width: 800px;
          height: 800px;
          background: radial-gradient(
            circle,
            rgba(207, 13, 29, 0.3) 0%,
            rgba(207, 13, 29, 0.15) 30%,
            transparent 70%
          );
          animation: redGlow1 8s ease-in-out infinite alternate;
          z-index: 1;
        }

        .red-glow-bottom-right {
          position: absolute;
          bottom: -150px;
          right: -150px;
          width: 600px;
          height: 600px;
          background: radial-gradient(
            circle,
            rgba(207, 13, 29, 0.25) 0%,
            rgba(207, 13, 29, 0.1) 30%,
            transparent 70%
          );
          animation: redGlow2 10s ease-in-out infinite alternate;
          z-index: 1;
        }

        @keyframes redGlow1 {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0.6;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            transform: translate(50px, 50px) scale(1.1);
            opacity: 0.6;
          }
        }

        @keyframes redGlow2 {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0.5;
          }
          50% {
            opacity: 0.7;
          }
          100% {
            transform: translate(-40px, -40px) scale(1.15);
            opacity: 0.5;
          }
        }

        /* Camada 2: Partículas */
        .particle {
          position: absolute;
          background: #CF0D1D;
          border-radius: 50%;
          box-shadow: 0 0 10px rgba(207, 13, 29, 0.5);
          opacity: 0.6;
          animation: rise linear infinite;
          z-index: 2;
          will-change: transform, opacity;
        }

        @keyframes rise {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }

        /* Camada 10: Card de login */
        .login-card-seguro {
          max-width: 480px;
          width: 100%;
          padding: 3rem 2.5rem;
          border-radius: 24px;
          background: rgba(28, 28, 28, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(207, 13, 29, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
          position: relative;
          z-index: 10;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1),
                      transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .login-card-seguro.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* Logo */
        .logo-container-seguro {
          display: flex;
          justify-content: center;
          margin-bottom: 3rem;
        }

        .logo-seguro {
          width: 240px;
          height: auto;
          filter: drop-shadow(0 4px 12px rgba(207, 13, 29, 0.4));
        }

        /* Campos de input */
        .input-wrapper-seguro {
          position: relative;
          margin-bottom: 1.25rem;
        }

        .input-icon-seguro {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.4);
          transition: color 0.3s ease;
          z-index: 1;
        }

        .input-seguro {
          width: 100%;
          height: 50px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 0 3rem 0 3rem;
          color: #ffffff !important;
          font-size: 0.95rem;
          transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
          -webkit-text-fill-color: #ffffff;
        }

        .input-seguro::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }

        .input-seguro:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.08);
          border-color: #CF0D1D;
          box-shadow: 
            0 0 0 3px rgba(207, 13, 29, 0.1),
            0 0 20px rgba(207, 13, 29, 0.2);
        }

        .input-seguro:focus + .input-icon-seguro {
          color: #CF0D1D;
        }

        .toggle-password-seguro {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.4);
          cursor: pointer;
          transition: color 0.3s ease;
          z-index: 1;
        }

        .toggle-password-seguro:hover {
          color: #CF0D1D;
        }

        /* Botão Entrar */
        .login-button-seguro {
          width: 100%;
          height: 50px;
          background: #CF0D1D;
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 16px rgba(207, 13, 29, 0.3);
          margin-top: 0.5rem;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
        }

        .login-button-seguro::before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          left: -100%;
          top: 0;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        .login-button-seguro:hover {
          background: #e60f21;
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(207, 13, 29, 0.4);
        }

        .login-button-seguro:hover::before {
          left: 100%;
        }

        .login-button-seguro:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(207, 13, 29, 0.3);
        }

        .login-button-seguro:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Footer */
        .forgot-password-seguro {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .forgot-link-seguro {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.875rem;
          text-decoration: none;
          transition: color 0.3s ease;
          margin-bottom: 0.25rem;
        }

        .forgot-link-seguro:hover {
          color: #CF0D1D;
        }

        .support-text-seguro {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.4);
        }

        .divider-seguro {
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding-top: 1rem;
          margin-top: 1rem;
        }

        .signup-text-seguro {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .signup-text-seguro span {
          color: rgba(255, 255, 255, 0.6);
        }

        .signup-link-seguro {
          color: #CF0D1D;
          font-weight: 600;
          text-decoration: none;
          position: relative;
          transition: color 0.3s ease;
        }

        .signup-link-seguro::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 2px;
          background: #CF0D1D;
          transition: width 0.3s ease;
        }

        .signup-link-seguro:hover::after {
          width: 100%;
        }

        /* Error message */
        .error-message-seguro {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          margin-bottom: 1.25rem;
        }

        /* Responsividade */
        @media (max-width: 640px) {
          .login-card-seguro {
            padding: 2rem 1.5rem;
            border-radius: 20px;
          }

          .logo-seguro {
            width: 200px;
          }

          .input-seguro,
          .login-button-seguro {
            height: 46px;
          }
        }
      `}</style>

      <div className="login-container-seguro">
        {/* Camada 0: Grid */}
        <div className="grid-background"></div>

        {/* Camada 1: Luzes vermelhas */}
        <div className="red-glow-top-left"></div>
        <div className="red-glow-bottom-right"></div>

        {/* Camada 2: Partículas */}
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.left}%`,
              bottom: '-10px',
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`
            }}
          />
        ))}

        {/* Camada 10: Card de Login */}
        <div className={`login-card-seguro ${fadeIn ? 'fade-in' : ''}`}>
          {/* Logo */}
          <div className="logo-container-seguro">
            <img src={logoSrc} alt="Logo" className="logo-seguro" />
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="error-message-seguro">
              {errorMessage}
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="input-wrapper-seguro">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-seguro"
                required
                disabled={loading}
              />
              <Mail className="input-icon-seguro" size={20} />
            </div>

            {/* Password */}
            <div className="input-wrapper-seguro">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-seguro"
                required
                disabled={loading}
              />
              <Lock className="input-icon-seguro" size={20} />
              <div
                className="toggle-password-seguro"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </div>
            </div>

            {/* Botão Entrar */}
            <button
              type="submit"
              className="login-button-seguro"
              disabled={loading}
            >
              {loading ? 'CARREGANDO...' : 'ENTRAR'}
            </button>
          </form>

          {/* Footer */}
          <div className="forgot-password-seguro">
            <a href="#" className="forgot-link-seguro">
              Esqueceu a senha?
            </a>
            <span className="support-text-seguro">Contate o suporte</span>
          </div>

          <div className="divider-seguro">
            <div className="signup-text-seguro">
              <span>Não tem uma conta?</span>
              <a href="/cadastro" className="signup-link-seguro">
                Cadastre-se
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginSeguro;
